import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Clock, Save, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';

const statusLabels = { abierto: 'Abierto', en_curso: 'En curso', esperando_cliente: 'Esperando cliente', resuelto: 'Resuelto', cerrado: 'Cerrado' };
const statusBadge = { abierto: 'badge-info', en_curso: 'badge-warning', esperando_cliente: 'badge-gray', resuelto: 'badge-success', cerrado: 'badge-gray' };
const priorityLabels = { baja: 'Baja', media: 'Media', alta: 'Alta', urgente: 'Urgente' };

// Estilo común de las etiquetas de los campos del panel "Detalles".
const FIELD_LABEL_STYLE = {
  fontSize: '0.78rem',
  color: '#475569',
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.04em',
  marginBottom: 6,
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [sending, setSending] = useState(false);
  const [expectedDate, setExpectedDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [reminderDraft, setReminderDraft] = useState({ periodDays: '', startDate: '', endDate: '' });
  const [savingReminder, setSavingReminder] = useState(false);
  const [sendCustomerDialog, setSendCustomerDialog] = useState(null);
  const [sendingToCustomer, setSendingToCustomer] = useState(false);
  const modal = useModal();

  useEffect(() => { loadTicket(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTicket = async () => {
    try {
      const [ticketRes, usersRes, customersRes] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get('/tickets/assignable-users'),
        api.get('/customers')
      ]);
      setTicket(ticketRes.data);
      setAssignableUsers(usersRes.data);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setExpectedDate(toDateInputValue(ticketRes.data.expectedCompletionDate));
      setCategoryDraft(ticketRes.data.category || '');
      setSubjectDraft(ticketRes.data.subject || '');
      setDescriptionDraft(ticketRes.data.description || '');
      setReminderDraft({
        periodDays: ticketRes.data.reminderPeriodDays != null ? String(ticketRes.data.reminderPeriodDays) : '',
        startDate: toDateInputValue(ticketRes.data.reminderStartDate),
        endDate: toDateInputValue(ticketRes.data.reminderEndDate),
      });
      if (ticketRes.data.customerId) {
        try {
          const contactsRes = await api.get(`/customers/${ticketRes.data.customerId}`);
          setCustomerContacts(Array.isArray(contactsRes.data?.contacts) ? contactsRes.data.contacts : []);
        } catch (e) {
          console.error('No se pudieron cargar los contactos del cliente', e);
          setCustomerContacts([]);
        }
      }
    } catch (error) {
      modal.error('Error cargando ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const res = await api.put(`/tickets/${id}`, { status });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error('Error actualizando estado');
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      const res = await api.put(`/tickets/${id}`, { priority });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando prioridad');
    }
  };

  const handleAssigneeChange = async (assignedTo) => {
    if (!assignedTo) {
      modal.error('Debes seleccionar un usuario asignado');
      return;
    }
    try {
      const res = await api.put(`/tickets/${id}`, { assignedTo: parseInt(assignedTo) });
      const newAssignee = assignableUsers.find(u => u.id === parseInt(assignedTo)) || null;
      setTicket(prev => ({ ...prev, ...res.data, assignee: newAssignee }));
    } catch (error) {
      modal.error('Error cambiando el asignado');
    }
  };

  const handleRemindAssigneeChange = async (value) => {
    try {
      const res = await api.put(`/tickets/${id}`, { remindAssignee: value === 'true' });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando el recordatorio');
    }
  };

  const saveReminderSchedule = async () => {
    setSavingReminder(true);
    try {
      const res = await api.put(`/tickets/${id}`, {
        reminderPeriodDays: reminderDraft.periodDays === '' ? null : parseInt(reminderDraft.periodDays, 10),
        reminderStartDate: reminderDraft.startDate || null,
        reminderEndDate: reminderDraft.endDate || null,
      });
      setTicket(prev => ({ ...prev, ...res.data }));
      modal.success('Programación del aviso guardada');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando la programación del aviso');
    } finally {
      setSavingReminder(false);
    }
  };

  const handleCategorySave = async () => {
    setSavingCategory(true);
    try {
      const res = await api.put(`/tickets/${id}`, { category: categoryDraft });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando categoría');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleExpectedDateSave = async () => {
    setSavingDate(true);
    try {
      const res = await api.put(`/tickets/${id}`, {
        expectedCompletionDate: expectedDate || null
      });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando fecha prevista');
    } finally {
      setSavingDate(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!subjectDraft.trim()) {
      modal.error('El asunto no puede estar vacío');
      return;
    }
    setSavingDetails(true);
    try {
      const res = await api.put(`/tickets/${id}`, { subject: subjectDraft.trim(), description: descriptionDraft });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando asunto y descripción');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleCustomerChange = async (customerId) => {
    const parsed = parseInt(customerId, 10);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    try {
      const res = await api.put(`/tickets/${id}`, { customerId: parsed, contactId: null });
      const newCustomer = customers.find(c => c.id === parsed) || ticket.customer || null;
      setTicket(prev => ({ ...prev, ...res.data, customer: newCustomer, contact: null }));
      try {
        const contactsRes = await api.get(`/customers/${parsed}`);
        setCustomerContacts(Array.isArray(contactsRes.data?.contacts) ? contactsRes.data.contacts : []);
      } catch (e) {
        console.error('No se pudieron cargar los contactos del cliente', e);
        setCustomerContacts([]);
      }
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error cambiando el cliente del ticket');
    }
  };

  const handleContactChange = async (contactId) => {
    try {
      const res = await api.put(`/tickets/${id}`, { contactId: contactId || null });
      const newContact = contactId ? (customerContacts.find(c => c.id === parseInt(contactId, 10)) || null) : null;
      setTicket(prev => ({ ...prev, ...res.data, contact: newContact }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error cambiando el contacto del ticket');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/tickets/${id}/comments`, { content: comment, isInternal });
      // El backend del POST /comments no devuelve user, lo enriquecemos en frontend.
      const enriched = { ...res.data, user: { id: res.data.userId, name: 'Tú' } };
      setTicket(prev => ({ ...prev, comments: [...(prev.comments || []), enriched] }));
      setComment('');
    } catch (error) {
      modal.error('Error añadiendo nota');
    } finally {
      setSending(false);
    }
  };

  const openSendCustomerDialog = async () => {
    try {
      const res = await api.get(`/tickets/${id}/send-to-customer/preview`);
      setSendCustomerDialog({
        recipient: res.data.customerEmail || '',
        sendCopy: true,
        preview: res.data,
      });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error preparando el envío');
    }
  };

  const handleConfirmSendCustomer = async () => {
    if (!sendCustomerDialog) return;
    setSendingToCustomer(true);
    try {
      await api.post(`/tickets/${id}/send-to-customer`, {
        email: sendCustomerDialog.recipient,
        sendCopyToSelf: sendCustomerDialog.sendCopy,
      });
      modal.success(`Ticket enviado al cliente (${sendCustomerDialog.recipient})`);
      setSendCustomerDialog(null);
      // Recargar para que aparezca la nota interna que registra el envío.
      loadTicket();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error enviando ticket al cliente');
    } finally {
      setSendingToCustomer(false);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatShortDate = (date) => new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const daysUntil = (date) => {
    if (!date) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return Math.round((target - now) / (1000 * 60 * 60 * 24));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>;
  if (!ticket) return null;

  const days = daysUntil(ticket.expectedCompletionDate);
  const dateColor = days !== null && days < 0 ? '#dc2626' : days !== null && days <= 2 ? '#b45309' : '#475569';
  const canEdit = hasPermission('tickets', 'edit');

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn-icon" onClick={() => navigate('/tickets')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{ticket.code} — {ticket.subject}</h1>
            <p className="page-subtitle">{ticket.customer?.name}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={`badge ${statusBadge[ticket.status]}`}>{statusLabels[ticket.status] || ticket.status}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        {/* Main content */}
        <div>
          {/* Asunto y descripción del ticket */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 className="card-title" style={{ marginBottom: 12 }}>Asunto y descripción</h2>
            {canEdit ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={FIELD_LABEL_STYLE}>Asunto</div>
                  <input
                    type="text"
                    className="form-input"
                    value={subjectDraft}
                    onChange={e => setSubjectDraft(e.target.value)}
                  />
                </div>
                <div>
                  <div style={FIELD_LABEL_STYLE}>Descripción</div>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={descriptionDraft}
                    onChange={e => setDescriptionDraft(e.target.value)}
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={savingDetails || !subjectDraft.trim() || (subjectDraft === ticket.subject && descriptionDraft === (ticket.description || ''))}
                    onClick={handleSaveDetails}
                  >
                    {savingDetails ? 'Guardando...' : 'Guardar asunto y descripción'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: 8 }}>{ticket.subject}</p>
                {ticket.description
                  ? <p style={{ whiteSpace: 'pre-wrap', color: '#475569', margin: 0 }}>{ticket.description}</p>
                  : <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>Sin descripción</p>}
              </>
            )}
          </div>

          {/* Comments */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <h2 className="card-title" style={{ margin: 0 }}>Conversación ({ticket.comments?.length || 0})</h2>
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={openSendCustomerDialog}
                  title="Envía al cliente todo el hilo del ticket excepto las notas internas"
                >
                  <Mail size={14} /> Enviar al cliente
                </button>
              )}
            </div>
            {ticket.comments?.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No hay notas aún</p>
            )}
            {ticket.comments?.map(c => {
              const authorName = c.user?.name || 'Usuario eliminado';
              return (
                <div key={c.id} style={{
                  padding: 16, marginBottom: 12, borderRadius: 8,
                  background: c.isInternal ? '#fef3c7' : '#f1f5f9',
                  borderLeft: `3px solid ${c.isInternal ? '#f59e0b' : '#3b82f6'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a' }}>
                      {authorName}
                      {c.isInternal && <span style={{ marginLeft: 8, color: '#92400e', fontSize: '0.72rem', fontWeight: 600, background: '#fde68a', padding: '2px 6px', borderRadius: 4 }}>NOTA INTERNA</span>}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{formatDate(c.createdAt)}</span>
                  </div>
                  <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.92rem', margin: 0 }}>{c.content}</p>
                </div>
              );
            })}

            {/* Add comment */}
            {canEdit && (
              <form onSubmit={handleComment} style={{ marginTop: 16, padding: 14, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ ...FIELD_LABEL_STYLE, marginBottom: 8 }}>Añadir nota al hilo</div>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Escribe una nota o anotación interna..."
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                    Nota interna (no se enviará al cliente cuando uses "Enviar al cliente")
                  </label>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !comment.trim()}>
                    <Save size={14} /> {sending ? 'Guardando...' : 'Guardar nota'}
                  </button>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 8, marginBottom: 0 }}>
                  Esto solo guarda la nota en el hilo. Para enviar el estado del ticket al cliente, usa el botón <strong>Enviar al cliente</strong> de arriba.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 className="card-title" style={{ marginBottom: 16 }}>Detalles</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={FIELD_LABEL_STYLE}>Estado</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.status} onChange={e => handleStatusChange(e.target.value)}>
                    <option value="abierto">Abierto</option>
                    <option value="en_curso">En curso</option>
                    <option value="esperando_cliente">Esperando cliente</option>
                    <option value="resuelto">Resuelto</option>
                    <option value="cerrado">Cerrado</option>
                  </select>
                ) : (
                  <span className={`badge ${statusBadge[ticket.status]}`}>{statusLabels[ticket.status]}</span>
                )}
              </div>

              <div>
                <div style={FIELD_LABEL_STYLE}>Asignado a</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.assignedTo || ''} onChange={e => handleAssigneeChange(e.target.value)}>
                    <option value="">— Seleccionar —</option>
                    {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                ) : (
                  <span style={{ fontWeight: 500 }}>{ticket.assignee?.name || '—'}</span>
                )}
              </div>

              <div>
                <div style={FIELD_LABEL_STYLE}>Recordatorio al asignado</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.remindAssignee === false ? 'false' : 'true'} onChange={e => handleRemindAssigneeChange(e.target.value)}>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <span style={{ fontWeight: 500 }}>{ticket.remindAssignee === false ? 'No' : 'Sí'}</span>
                )}
              </div>

              {ticket.remindAssignee !== false && (
                <>
                  <div>
                    <div style={FIELD_LABEL_STYLE}>Aviso: cada cuántos días</div>
                    {canEdit ? (
                      <input type="number" min="1" step="1" className="form-input" placeholder="vacío = un solo aviso"
                        value={reminderDraft.periodDays}
                        onChange={e => setReminderDraft(d => ({ ...d, periodDays: e.target.value }))} />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{ticket.reminderPeriodDays != null ? `cada ${ticket.reminderPeriodDays} día(s)` : 'un solo aviso'}</span>
                    )}
                  </div>
                  <div>
                    <div style={FIELD_LABEL_STYLE}>Aviso: desde</div>
                    {canEdit ? (
                      <input type="date" className="form-input"
                        value={reminderDraft.startDate}
                        onChange={e => setReminderDraft(d => ({ ...d, startDate: e.target.value }))} />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{ticket.reminderStartDate ? formatShortDate(ticket.reminderStartDate) : '—'}</span>
                    )}
                  </div>
                  <div>
                    <div style={FIELD_LABEL_STYLE}>Aviso: hasta</div>
                    {canEdit ? (
                      <input type="date" className="form-input"
                        value={reminderDraft.endDate}
                        onChange={e => setReminderDraft(d => ({ ...d, endDate: e.target.value }))} />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{ticket.reminderEndDate ? formatShortDate(ticket.reminderEndDate) : 'sin fin'}</span>
                    )}
                  </div>
                  {canEdit && (
                    <div>
                      <button type="button" className="btn btn-primary btn-sm"
                        disabled={savingReminder
                          || (reminderDraft.periodDays === (ticket.reminderPeriodDays != null ? String(ticket.reminderPeriodDays) : '')
                            && reminderDraft.startDate === toDateInputValue(ticket.reminderStartDate)
                            && reminderDraft.endDate === toDateInputValue(ticket.reminderEndDate))}
                        onClick={saveReminderSchedule}>
                        {savingReminder ? 'Guardando...' : 'Guardar programación del aviso'}
                      </button>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 }}>
                        Solo se envía si el avisador automático está activado y el estado del ticket está entre los seleccionados (Tickets → Avisos).
                      </div>
                    </div>
                  )}
                </>
              )}

              <div>
                <div style={FIELD_LABEL_STYLE}>Propietario</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.ownerId || ''} onChange={async (e) => {
                    const ownerId = e.target.value ? parseInt(e.target.value, 10) : null;
                    try {
                      const res = await api.put(`/tickets/${id}`, { ownerId });
                      setTicket(prev => ({ ...prev, ownerId: res.data.ownerId }));
                    } catch (err) {
                      modal.error(err.response?.data?.message || 'Error cambiando propietario');
                    }
                  }}>
                    <option value="">— Sin cambiar —</option>
                    {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                ) : (
                  <span style={{ fontWeight: 500 }}>{assignableUsers.find(u => u.id === ticket.ownerId)?.name || '—'}</span>
                )}
              </div>

              <div>
                <div style={FIELD_LABEL_STYLE}>
                  Fecha prevista de finalización
                </div>
                {canEdit ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="date"
                      className="form-input"
                      value={expectedDate}
                      onChange={e => setExpectedDate(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={savingDate || expectedDate === toDateInputValue(ticket.expectedCompletionDate)}
                      onClick={handleExpectedDateSave}
                    >
                      Guardar
                    </button>
                  </div>
                ) : (
                  <span style={{ fontWeight: 500 }}>
                    {ticket.expectedCompletionDate ? formatShortDate(ticket.expectedCompletionDate) : '—'}
                  </span>
                )}
                {ticket.expectedCompletionDate && (
                  <div style={{ fontSize: '0.78rem', color: dateColor, marginTop: 4, fontWeight: 500 }}>
                    {days < 0
                      ? `Faltan -${Math.abs(days)} días (retraso)`
                      : days === 0
                        ? 'Vence hoy'
                        : `Faltan ${days} días`}
                  </div>
                )}
              </div>

              <div>
                <div style={FIELD_LABEL_STYLE}>Prioridad</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.priority} onChange={e => handlePriorityChange(e.target.value)}>
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                ) : (
                  <span style={{ fontWeight: 500 }}>{priorityLabels[ticket.priority] || ticket.priority}</span>
                )}
              </div>
              <div>
                <div style={FIELD_LABEL_STYLE}>Categoría</div>
                {canEdit ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      className="form-input"
                      value={categoryDraft}
                      onChange={e => setCategoryDraft(e.target.value)}
                      placeholder="Ej: Soporte técnico"
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={savingCategory || categoryDraft === (ticket.category || '')}
                      onClick={handleCategorySave}
                    >
                      Guardar
                    </button>
                  </div>
                ) : (
                  <span>{ticket.category || '—'}</span>
                )}
              </div>
              <div>
                <div style={FIELD_LABEL_STYLE}>Cliente</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.customerId || ''} onChange={e => handleCustomerChange(e.target.value)}>
                    {!customers.some(c => c.id === ticket.customerId) && ticket.customer && (
                      <option value={ticket.customerId}>{ticket.customer.name}</option>
                    )}
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <span style={{ fontWeight: 500 }}>{ticket.customer?.name}</span>
                )}
              </div>
              <div>
                <div style={FIELD_LABEL_STYLE}>Contacto</div>
                {canEdit ? (
                  <select className="form-input" value={ticket.contactId || ''} onChange={e => handleContactChange(e.target.value)}>
                    <option value="">— Sin contacto —</option>
                    {customerContacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                  </select>
                ) : (
                  <span>{ticket.contact ? `${ticket.contact.firstName} ${ticket.contact.lastName}` : '—'}</span>
                )}
              </div>
              <div>
                <div style={FIELD_LABEL_STYLE}>Creado</div>
                <span style={{ fontSize: '0.85rem' }}><Clock size={12} style={{ display: 'inline', marginRight: 4 }} />{formatDate(ticket.createdAt)}</span>
              </div>
              {ticket.lastReminderSentAt && (
                <div>
                  <div style={FIELD_LABEL_STYLE}>Último aviso enviado</div>
                  <span style={{ fontSize: '0.85rem' }}>{formatDate(ticket.lastReminderSentAt)}</span>
                </div>
              )}
              {ticket.resolvedAt && (
                <div>
                  <div style={FIELD_LABEL_STYLE}>Resuelto</div>
                  <span style={{ fontSize: '0.85rem' }}>{formatDate(ticket.resolvedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {sendCustomerDialog && (
        <div className="modal-overlay" onClick={() => !sendingToCustomer && setSendCustomerDialog(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="modal-header">
              <h2>Enviar ticket {sendCustomerDialog.preview?.ticketCode || ticket.code} al cliente</h2>
              <button className="btn-icon" onClick={() => setSendCustomerDialog(null)} disabled={sendingToCustomer}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 10, columnGap: 12, fontSize: 14 }}>
                <strong>Remitente:</strong>
                <span>
                  {sendCustomerDialog.preview?.senderName ? `${sendCustomerDialog.preview.senderName} ` : ''}
                  &lt;{sendCustomerDialog.preview?.senderEmail || '—'}&gt;
                </span>
                <strong>Destinatario:</strong>
                <input
                  type="email"
                  className="form-input"
                  value={sendCustomerDialog.recipient}
                  onChange={e => setSendCustomerDialog(prev => ({ ...prev, recipient: e.target.value }))}
                  disabled={sendingToCustomer}
                  placeholder="cliente@ejemplo.com"
                />
                <strong>Contenido:</strong>
                <span style={{ color: '#475569' }}>
                  Estado del ticket, descripción y <strong>{sendCustomerDialog.preview?.visibleComments || 0}</strong> nota(s) del hilo.
                  {sendCustomerDialog.preview?.internalComments > 0 && (
                    <span style={{ color: '#92400e' }}> {sendCustomerDialog.preview.internalComments} nota(s) interna(s) NO se incluirán.</span>
                  )}
                </span>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendCustomerDialog.sendCopy}
                  onChange={e => setSendCustomerDialog(prev => ({ ...prev, sendCopy: e.target.checked }))}
                  disabled={sendingToCustomer}
                />
                Enviarme también una copia (CCO al remitente)
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setSendCustomerDialog(null)} disabled={sendingToCustomer}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmSendCustomer} disabled={sendingToCustomer || !sendCustomerDialog.recipient}>
                {sendingToCustomer ? 'Enviando...' : 'Confirmar y enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
